import React from "react";
import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import { radii } from "../../../theme/shape";

const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp", ".svg"];
const VIDEO_EXTENSIONS = [".mp4", ".mov", ".webm", ".avi", ".mkv"];
const DOUBLE_BRACE_VARIABLE_REGEX = /^\{\{[^{}]+\}\}$/;
const BRACKET_VARIABLE_REGEX = new RegExp("^\\[[^\\[\\]\\s]+\\]$");
const VARIABLE_TOKEN_SPLIT_REGEX = new RegExp("(\\{\\{[^{}]+\\}\\}|\\[[^\\[\\]]+\\])", "g");
const WHATSAPP_INLINE_TOKEN_SPLIT_REGEX = /(```[\s\S]*?```|`[^`\n]+`|\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|\{\{[^{}]+\}\}|\[[^[\]]+\])/g;
const CODE_FONT_FAMILY = '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace';

function normalizeChannel(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (["wpp", "whatsapp", "whatsapp_api"].includes(normalized)) return "whatsapp";
  if (normalized === "sms") return "sms";
  if (normalized === "email" || normalized === "e-mail") return "email";
  return normalized || "whatsapp";
}

function getPreviewSurfaceColor(theme) {
  return theme.palette.mode === "dark"
    ? alpha(theme.palette.custom.blue[900], 0.22)
    : theme.palette.custom.blue[50];
}

function getPreviewBorderColor(theme) {
  return alpha(theme.palette.custom.blue[200], theme.palette.mode === "dark" ? 0.28 : 0.7);
}

function firstText(...values) {
  const found = values.find((value) => String(value || "").trim());
  return found ? String(found).trim() : "";
}

function pick(value, keys) {
  if (!value || typeof value !== "object") return "";
  const foundKey = keys.find((key) => value[key] !== undefined && value[key] !== null && String(value[key]).trim());
  return foundKey ? value[foundKey] : "";
}

function stripHtml(value) {
  return String(value || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function inferMediaKind(raw, fallbackType = "") {
  const type = String(
    fallbackType || pick(raw, ["kind", "mediaType", "type", "format", "fileType", "mimeType", "mimetype"]),
  ).toLowerCase();
  const url = String(pick(raw, ["url", "fileUrl", "src", "link", "path", "content"]) || "").toLowerCase();
  const name = String(pick(raw, ["name", "fileName", "filename", "title"]) || "").toLowerCase();
  const source = `${type} ${url} ${name}`;

  if (source.includes("pdf") || source.includes("document")) return "document";
  if (source.includes("image") || IMAGE_EXTENSIONS.some((extension) => source.includes(extension))) return "image";
  if (source.includes("video") || VIDEO_EXTENSIONS.some((extension) => source.includes(extension))) return "video";
  return source ? "file" : "";
}

function normalizeMedia(raw, fallbackType = "") {
  if (!raw) return null;

  if (typeof raw === "string") {
    const kind = inferMediaKind({ url: raw }, fallbackType);
    return kind
      ? {
          id: raw,
          kind,
          url: raw,
          name: kind === "document" ? "Documento" : "Arquivo",
        }
      : null;
  }

  if (typeof raw !== "object") return null;

  const kind = inferMediaKind(raw, fallbackType);
  if (!kind) return null;

  const url = firstText(pick(raw, ["url", "fileUrl", "src", "link", "path", "content"]));
  const name = firstText(pick(raw, ["name", "fileName", "filename", "title"]), kind === "document" ? "Documento" : "Arquivo");
  const size = firstText(pick(raw, ["sizeLabel", "fileSizeLabel"]), formatFileSize(pick(raw, ["size", "fileSize", "bytes"])));
  const action = firstText(pick(raw, ["action"]));

  return {
    action,
    id: firstText(pick(raw, ["id", "_id"]), `${kind}-${name}-${url}`),
    kind,
    name,
    size,
    url,
  };
}

function normalizeHeader(preview) {
  const header = preview?.header && typeof preview.header === "object" ? preview.header : null;
  const headerComponent = Array.isArray(preview?.components)
    ? preview.components.find((component) => String(component?.type || "").toLowerCase() === "header")
    : null;
  const source = header || headerComponent;

  if (!source || typeof source !== "object") return { text: "", media: null };

  const type = String(source.type || source.format || "").trim().toLowerCase();
  const parameter = Array.isArray(source.parameters)
    ? source.parameters.find((item) => item?.document || item?.image || item?.video || item?.text)
    : null;

  if (parameter?.document) return { text: "", media: normalizeMedia(parameter.document, "document") };
  if (parameter?.image) return { text: "", media: normalizeMedia(parameter.image, "image") };
  if (parameter?.video) return { text: "", media: normalizeMedia(parameter.video, "video") };
  if (parameter?.text) return { text: String(parameter.text), media: null };

  const nested = type && source[type] && typeof source[type] === "object" ? source[type] : source;

  if (type === "text") {
    return { text: firstText(source.text, source.content), media: null };
  }

  return {
    text: "",
    media: normalizeMedia(nested, type),
  };
}

function normalizeAttachments(preview, headerMedia) {
  const rawAttachments = [
    ...(Array.isArray(preview?.attachments) ? preview.attachments : []),
    ...(Array.isArray(preview?.files) ? preview.files : []),
  ];
  const attachments = rawAttachments.map((attachment) => normalizeMedia(attachment)).filter(Boolean);
  const orderedAttachments = attachments.length ? attachments : (headerMedia ? [headerMedia] : []);
  const seen = new Set();

  return orderedAttachments.filter((attachment) => {
    const urlKey = String(attachment.url || "").trim().toLowerCase();
    const nameKey = String(attachment.name || "").trim().toLowerCase();
    const hasStableIdentity = Boolean(urlKey || (nameKey && !["arquivo", "documento"].includes(nameKey)));

    if (!hasStableIdentity) return true;

    const key = [
      String(attachment.kind || "").toLowerCase(),
      urlKey,
      nameKey,
    ].join("|");

    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function normalizeLookupKey(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function findVariableValue(variables, keys) {
  if (!variables || typeof variables !== "object") return "";

  const normalizedKeys = keys.map(normalizeLookupKey);
  const entries = Object.entries(variables);
  const exactMatch = entries.find(([key, value]) =>
    value !== undefined && value !== null && normalizedKeys.includes(normalizeLookupKey(key)),
  );

  if (exactMatch) return String(exactMatch[1]).trim();

  const partialMatch = entries.find(([key, value]) => {
    const normalizedKey = normalizeLookupKey(key);
    return value !== undefined && value !== null && normalizedKeys.some((candidate) => normalizedKey.includes(candidate));
  });

  return partialMatch ? String(partialMatch[1]).trim() : "";
}

function getWhatsAppOpenUrl(recipientContact, variables) {
  const phone = firstText(
    recipientContact,
    findVariableValue(variables, [
      "WhatsApp",
      "Whatsapp",
      "Telefone",
      "Telefone_01",
      "Telefone_02",
      "Telefone_03",
      "Celular",
    ]),
  );
  const digits = phone.replace(/\D/g, "").replace(/^0+/, "");

  if (digits.length < 10) return "";
  const normalizedDigits = digits.startsWith("55") ? digits : `55${digits}`;

  return `https://wa.me/${normalizedDigits}`;
}

function getButtonLabel(button) {
  const label = firstText(button?.label, button?.text, button?.title);
  if (label) return label;

  const subType = String(button?.sub_type || button?.type || "").trim().toLowerCase();
  const paymentMethods = Array.isArray(button?.payment_methods) ? button.payment_methods : [];

  if (subType === "order_details") {
    if (paymentMethods.includes("pix_dynamic_code") && paymentMethods.length === 1) return "Copiar chave PIX";
    if (paymentMethods.includes("boleto") && paymentMethods.length === 1) return "Linha digitável";
    return "Detalhes de pagamento";
  }

  if (subType === "url") return "Abrir link";
  if (subType === "phone_number") return "Ligar";
  return "Responder";
}

function getButtonCopyKind(button, label) {
  const explicitCopyKind = firstText(button?.copy_kind, button?.copyKind);
  if (explicitCopyKind) return explicitCopyKind;

  const subType = String(button?.sub_type || button?.type || "").trim().toLowerCase();
  const paymentMethods = Array.isArray(button?.payment_methods) ? button.payment_methods : [];
  const normalizedLabel = normalizeLookupKey(label);

  if (normalizedLabel.includes("pix")) return "pix";
  if (
    normalizedLabel.includes("boleto") ||
    normalizedLabel.includes("barra") ||
    normalizedLabel.includes("digitavel") ||
    normalizedLabel.includes("linha")
  ) {
    return "barcode";
  }

  if (subType === "order_details") {
    if (paymentMethods.includes("pix_dynamic_code")) return "pix";
    if (paymentMethods.includes("boleto")) return "barcode";
  }

  return "";
}

function getButtonCopyValue(button, variables, copyKind) {
  const explicitValue = firstText(
    button?.copy_value,
    button?.copyValue,
    button?.value,
    button?.payload,
    button?.code,
    button?.pix_code,
    button?.pixCode,
    button?.barcode,
    button?.barCode,
    button?.digitable_line,
    button?.digitableLine,
    button?.line,
    button?.payment_data?.pix_code,
    button?.payment_data?.barcode,
    button?.payment_data?.digitable_line,
  );

  if (explicitValue) return explicitValue;

  if (copyKind === "pix") {
    return findVariableValue(variables, [
      "Código_PIX",
      "Codigo_PIX",
      "Código Pix",
      "Codigo Pix",
      "Chave_PIX",
      "Chave Pix",
      "PIX",
      "Pix",
      "Pix_Copia_Cola",
      "QRCode_PIX",
      "QR_Code_PIX",
    ]);
  }

  if (copyKind === "barcode") {
    return findVariableValue(variables, [
      "Código_Barras",
      "Codigo_Barras",
      "Código Barras",
      "Codigo Barras",
      "Código de Barras",
      "Codigo de Barras",
      "Linha_Digitável",
      "Linha_Digitavel",
      "Linha Digitável",
      "Linha Digitavel",
      "Boleto",
    ]);
  }

  return "";
}

function normalizeButtonAction(button) {
  const action = String(button?.action || "").trim().toLowerCase();

  if (action === "open_url") return "url";
  if (action === "call_phone") return "phone";
  if (action === "copy") return "copy";
  if (action === "reply") return "reply";
  if (action === "payment_details") return "payment_details";

  return "";
}

function normalizeLegacyButtonAction(button, label, copyKind) {
  const subType = String(button?.sub_type || button?.type || "").trim().toLowerCase();
  const paymentMethods = Array.isArray(button?.payment_methods) ? button.payment_methods : [];
  const url = firstText(button?.url, button?.url_suffix, button?.href, button?.link);

  if (subType === "url" || url) return "url";
  if (subType === "phone_number" || subType === "phone") return "phone";
  if (
    copyKind &&
    (subType === "order_details" ||
      subType.includes("copy") ||
      paymentMethods.length ||
      normalizeLookupKey(label).includes("copiar"))
  ) {
    return "copy";
  }

  return "reply";
}

function normalizeButton(button, index, variables) {
  const label = getButtonLabel(button);
  const copyKind = getButtonCopyKind(button, label);
  const contractAction = normalizeButtonAction(button);
  const type = contractAction || normalizeLegacyButtonAction(button, label, copyKind);

  return {
    copyKind,
    copyValue: type === "copy" ? getButtonCopyValue(button, variables, copyKind) : "",
    id: firstText(button?.id, `${button?.action || button?.sub_type || button?.type || "delivered-button"}-${index}-${label}`),
    label,
    phone: firstText(button?.phone_number, button?.phoneNumber, button?.phone),
    type,
    url: firstText(button?.url, button?.url_suffix, button?.href, button?.link),
  };
}

function normalizePaymentAction(action, buttonIndex, actionIndex, variables) {
  return normalizeButton(
    {
      ...action,
      action: action?.action || "copy",
      id: firstText(action?.id, `payment-action-${buttonIndex}-${actionIndex}-${action?.copy_kind || action?.label || "copy"}`),
    },
    `${buttonIndex}-${actionIndex}`,
    variables,
  );
}

function normalizeButtons(preview, variables) {
  const buttons = [];
  const rawButtons = Array.isArray(preview?.buttons) ? preview.buttons : [];
  const topLevelPaymentActions = Array.isArray(preview?.payment_actions) ? preview.payment_actions : [];

  rawButtons.forEach((button, index) => {
    const paymentActions = Array.isArray(button?.payment_actions) ? button.payment_actions : [];

    if (paymentActions.length) {
      paymentActions.forEach((paymentAction, actionIndex) => {
        buttons.push(normalizePaymentAction(paymentAction, index, actionIndex, variables));
      });
      return;
    }

    buttons.push(normalizeButton(button, index, variables));
  });

  topLevelPaymentActions.forEach((paymentAction, index) => {
    buttons.push(normalizePaymentAction(paymentAction, "top", index, variables));
  });

  return buttons.filter((button) => button.label);
}

function formatFileSize(value) {
  const size = Number(value);
  if (!Number.isFinite(size) || size <= 0) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 102.4) / 10} KB`;
  return `${Math.round(size / 1024 / 102.4) / 10} MB`;
}

function formatTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getMessageText(preview) {
  const body = preview?.body && typeof preview.body === "object" ? preview.body : {};
  const legacyBody = preview?.body && typeof preview.body !== "object" ? preview.body : "";
  const text = firstText(
    preview?.rendered_text,
    body.rendered_text,
    body.text,
    preview?.text,
    preview?.content,
    legacyBody,
    preview?.template_text,
    body.template_text,
  );
  if (text) return text;
  if (preview?.html) return stripHtml(preview.html);
  return firstText(preview?.subject);
}

export function getDeliveredPreviewText(preview) {
  const source = preview && typeof preview === "object" ? preview : {};
  return getMessageText(source);
}

function normalizeActions(preview) {
  return (Array.isArray(preview?.actions) ? preview.actions : [])
    .map((action, index) => ({
      action: String(action?.action || "").trim().toLowerCase(),
      id: firstText(action?.id, `preview-action-${index}-${action?.action || "action"}`),
      label: firstText(action?.label, action?.text, action?.title),
      url: firstText(action?.url, action?.href, action?.link),
    }))
    .filter((action) => action.action && (action.label || action.url));
}

function getPreviewActionUrl(actions, actionName) {
  const found = actions.find((action) => action.action === actionName && action.url);
  return found ? found.url : "";
}

function normalizeDeliveredPreview(preview, fallbackChannel, fallbackSentAt, fallbackStatus, fallbackError, variables) {
  const source = preview && typeof preview === "object" ? preview : {};
  const header = normalizeHeader(source);
  const actions = normalizeActions(source);

  return {
    actions,
    attachments: normalizeAttachments(source, header.media),
    buttons: normalizeButtons(source, variables),
    channel: normalizeChannel(source.channel || fallbackChannel),
    error: firstText(source.error, source.error_message, source.errorMessage, fallbackError),
    footer: firstText(source.footer?.text, source.footer),
    headerText: header.text,
    sentAt: firstText(source.sent_at, source.created_at, fallbackSentAt),
    status: firstText(
      source.status?.code,
      source.status?.label,
      source.meta_status?.code,
      source.meta_status?.label,
      source.delivery_status?.code,
      source.delivery_status?.label,
      typeof source.status === "object" ? "" : source.status,
      fallbackStatus,
    ),
    subject: firstText(source.subject),
    text: getMessageText(source),
    whatsappUrl: firstText(source.whatsapp_url, source.whatsappUrl, getPreviewActionUrl(actions, "open_whatsapp")),
  };
}

function hasPreviewContent(message) {
  return Boolean(
    message.text ||
      message.headerText ||
      message.footer ||
      message.subject ||
      message.error ||
      message.attachments.length ||
      message.buttons.length ||
      message.actions.length,
  );
}

function isVariableToken(value) {
  return DOUBLE_BRACE_VARIABLE_REGEX.test(value) || BRACKET_VARIABLE_REGEX.test(value);
}

function variableTokenSx(theme) {
  return {
    color: theme.palette.error.main,
    fontWeight: 700,
  };
}

function inlineCodeSx(theme) {
  return {
    backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.16 : 0.08),
    borderRadius: 0.75,
    fontFamily: CODE_FONT_FAMILY,
    fontSize: "0.94em",
    px: 0.4,
  };
}

function renderTextWithVariables(text, theme, keyPrefix = "text") {
  const source = String(text || "");
  if (!source) return null;

  return source.split(VARIABLE_TOKEN_SPLIT_REGEX).map((part, index) => {
    if (!part) return null;
    const isVariable = isVariableToken(part);

    return (
      <Box
        component="span"
        key={`${keyPrefix}-${index}`}
        sx={isVariable ? variableTokenSx(theme) : undefined}
      >
        {part}
      </Box>
    );
  });
}

function renderWhatsAppInlineText(text, theme, keyPrefix = "wa-inline") {
  const source = String(text || "");
  if (!source) return null;

  return source.split(WHATSAPP_INLINE_TOKEN_SPLIT_REGEX).map((part, index) => {
    if (!part) return null;

    const key = `${keyPrefix}-${index}`;
    const inner = part.slice(1, -1);

    if (isVariableToken(part)) {
      return (
        <Box component="span" key={key} sx={variableTokenSx(theme)}>
          {part}
        </Box>
      );
    }

    if (part.startsWith("```") && part.endsWith("```") && part.length > 6) {
      return (
        <Box component="span" key={key} sx={inlineCodeSx(theme)}>
          {part.slice(3, -3)}
        </Box>
      );
    }

    if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
      return (
        <Box component="code" key={key} sx={inlineCodeSx(theme)}>
          {inner}
        </Box>
      );
    }

    if (part.startsWith("*") && part.endsWith("*") && inner.trim()) {
      return (
        <Box component="strong" key={key} sx={{ fontWeight: 800 }}>
          {renderWhatsAppInlineText(inner, theme, `${key}-bold`)}
        </Box>
      );
    }

    if (part.startsWith("_") && part.endsWith("_") && inner.trim()) {
      return (
        <Box component="em" key={key} sx={{ fontStyle: "italic" }}>
          {renderWhatsAppInlineText(inner, theme, `${key}-italic`)}
        </Box>
      );
    }

    if (part.startsWith("~") && part.endsWith("~") && inner.trim()) {
      return (
        <Box component="span" key={key} sx={{ textDecoration: "line-through" }}>
          {renderWhatsAppInlineText(inner, theme, `${key}-strike`)}
        </Box>
      );
    }

    return (
      <Box component="span" key={key}>
        {part}
      </Box>
    );
  });
}

function classifyWhatsAppLine(line) {
  if (!line) return { type: "blank" };

  const quoteMatch = line.match(/^\s*>\s?(.*)$/);
  if (quoteMatch) return { text: quoteMatch[1], type: "quote" };

  const bulletMatch = line.match(/^\s*[-*]\s+(.+)$/);
  if (bulletMatch) return { marker: "•", text: bulletMatch[1], type: "list_item" };

  const numberMatch = line.match(/^\s*(\d+)\.\s+(.+)$/);
  if (numberMatch) return { marker: `${numberMatch[1]}.`, text: numberMatch[2], type: "list_item" };

  return { text: line, type: "text" };
}

function parseWhatsAppTextBlocks(text) {
  const lines = String(text || "").replace(/\r\n?/g, "\n").split("\n");
  const blocks = [];
  let codeLines = null;

  lines.forEach((line) => {
    const trimmed = line.trim();

    if (codeLines) {
      if (trimmed.endsWith("```")) {
        const closingIndex = line.lastIndexOf("```");
        codeLines.push(line.slice(0, closingIndex));
        blocks.push({ text: codeLines.join("\n"), type: "code_block" });
        codeLines = null;

        const trailing = line.slice(closingIndex + 3);
        if (trailing.trim()) blocks.push(classifyWhatsAppLine(trailing));
        return;
      }

      codeLines.push(line);
      return;
    }

    if (trimmed.startsWith("```")) {
      const openingIndex = line.indexOf("```");
      const afterOpening = line.slice(openingIndex + 3);
      const closingIndex = afterOpening.indexOf("```");

      if (closingIndex >= 0) {
        blocks.push({ text: afterOpening.slice(0, closingIndex), type: "code_block" });
        const trailing = afterOpening.slice(closingIndex + 3);
        if (trailing.trim()) blocks.push(classifyWhatsAppLine(trailing));
        return;
      }

      codeLines = [afterOpening];
      return;
    }

    blocks.push(classifyWhatsAppLine(line));
  });

  if (codeLines) blocks.push({ text: codeLines.join("\n"), type: "code_block" });

  return blocks;
}

function renderWhatsAppBlock(block, theme, keyPrefix) {
  if (block.type === "blank") {
    return <Box component="span" key={keyPrefix} sx={{ display: "block", height: 8 }} />;
  }

  if (block.type === "code_block") {
    return (
      <Box
        component="pre"
        key={keyPrefix}
        sx={{
          backgroundColor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.16 : 0.08),
          borderRadius: radii.base,
          display: "block",
          fontFamily: CODE_FONT_FAMILY,
          fontSize: 12,
          lineHeight: 1.45,
          my: 0.75,
          overflowX: "auto",
          p: 1,
          whiteSpace: "pre-wrap",
        }}
      >
        {block.text}
      </Box>
    );
  }

  if (block.type === "quote") {
    return (
      <Box
        component="span"
        key={keyPrefix}
        sx={{
          borderLeft: `3px solid ${alpha(theme.palette.text.secondary, 0.4)}`,
          color: theme.palette.text.secondary,
          display: "block",
          pl: 1,
        }}
      >
        {renderWhatsAppInlineText(block.text, theme, `${keyPrefix}-quote`)}
      </Box>
    );
  }

  if (block.type === "list_item") {
    return (
      <Box
        component="span"
        key={keyPrefix}
        sx={{
          alignItems: "baseline",
          display: "flex",
          gap: 0.75,
          pl: 0.5,
        }}
      >
        <Box
          component="span"
          sx={{
            color: theme.palette.text.secondary,
            flex: "0 0 20px",
            textAlign: "right",
          }}
        >
          {block.marker}
        </Box>
        <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
          {renderWhatsAppInlineText(block.text, theme, `${keyPrefix}-item`)}
        </Box>
      </Box>
    );
  }

  return (
    <Box component="span" key={keyPrefix} sx={{ display: "block" }}>
      {renderWhatsAppInlineText(block.text, theme, `${keyPrefix}-text`)}
    </Box>
  );
}

function renderWhatsAppFormattedText(text, theme, keyPrefix = "whatsapp-text") {
  return parseWhatsAppTextBlocks(text).map((block, index) => renderWhatsAppBlock(block, theme, `${keyPrefix}-${index}`));
}

function renderPreviewText(text, theme, channel, keyPrefix) {
  if (normalizeChannel(channel) === "whatsapp") {
    return renderWhatsAppFormattedText(text, theme, keyPrefix);
  }

  return renderTextWithVariables(text, theme, keyPrefix);
}

function MediaIcon({ kind }) {
  if (kind === "image") return <ImageOutlinedIcon sx={{ color: "text.secondary", fontSize: 22 }} />;
  if (kind === "document") return <PictureAsPdfOutlinedIcon sx={{ color: "error.main", fontSize: 24 }} />;
  return <InsertDriveFileOutlinedIcon sx={{ color: "text.secondary", fontSize: 22 }} />;
}

function MessageAttachment({ attachment }) {
  const theme = useTheme();
  const hasOpenUrl = Boolean(attachment.url && !/\{\{.*\}\}/.test(String(attachment.url)));
  const isImage = attachment.kind === "image" && hasOpenUrl;

  if (isImage) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.custom.blue[200], 0.7)}`,
          borderRadius: radii.base,
          mb: 1,
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src={attachment.url}
          alt={attachment.name || "Imagem da mensagem"}
          sx={{
            display: "block",
            maxHeight: 210,
            objectFit: "cover",
            width: "100%",
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        alignItems: "center",
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.custom.blue[200], 0.7)}`,
        borderRadius: radii.base,
        display: "flex",
        gap: 1,
        mb: 1,
        minWidth: 0,
        px: 1,
        py: 0.75,
      }}
    >
      <MediaIcon kind={attachment.kind} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            color: theme.palette.text.primary,
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1.25,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {attachment.name}
        </Typography>
        {attachment.size ? (
          <Typography sx={{ color: theme.palette.text.secondary, fontSize: 10.5, lineHeight: 1.2 }}>
            {attachment.size}
          </Typography>
        ) : null}
      </Box>
      {hasOpenUrl ? (
        <IconButton
          aria-label={`Abrir ${attachment.name || "arquivo"} em nova aba`}
          component="a"
          href={attachment.url}
          rel="noopener noreferrer"
          target="_blank"
          sx={{
            color: theme.palette.text.secondary,
            flex: "0 0 auto",
            height: 30,
            width: 30,
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.text.primary,
            },
          }}
        >
          <OpenInNewOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      ) : null}
    </Box>
  );
}

function TemplateButtonIcon({ type }) {
  if (type === "url") return <OpenInNewOutlinedIcon sx={{ fontSize: 17 }} />;
  if (type === "phone") return <PhoneOutlinedIcon sx={{ fontSize: 17 }} />;
  if (type === "copy") return <ContentCopyOutlinedIcon sx={{ fontSize: 17 }} />;
  return <ReplyOutlinedIcon sx={{ fontSize: 17 }} />;
}

function copyWithTextareaFallback(text) {
  if (typeof document === "undefined") {
    return Promise.reject(new Error("clipboard-unavailable"));
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";
  document.body.appendChild(textarea);
  textarea.select();

  try {
    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    return copied ? Promise.resolve() : Promise.reject(new Error("clipboard-copy-failed"));
  } catch (error) {
    document.body.removeChild(textarea);
    return Promise.reject(error);
  }
}

function copyToClipboard(value) {
  const text = String(value || "");

  if (!text) return Promise.reject(new Error("empty-copy-value"));

  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text).catch(() => copyWithTextareaFallback(text));
  }

  return copyWithTextareaFallback(text);
}

function TemplateButton({ button, copyStatus, onCopy }) {
  const theme = useTheme();
  const isCopyButton = button.type === "copy";
  const isUrlButton = button.type === "url" && button.url;
  const isPhoneButton = button.type === "phone" && button.phone;
  const isInteractive = isCopyButton || isUrlButton || isPhoneButton;
  const disabled = isCopyButton && !button.copyValue;
  const label = copyStatus === "success"
    ? "Copiado"
    : copyStatus === "error"
      ? "Erro ao copiar"
      : button.label;

  return (
    <Box
      aria-label={isCopyButton ? `${button.label}${disabled ? " indisponível" : ""}` : undefined}
      component={isCopyButton ? "button" : (isUrlButton || isPhoneButton ? "a" : "div")}
      disabled={isCopyButton ? disabled : undefined}
      href={isUrlButton ? button.url : (isPhoneButton ? `tel:${String(button.phone).replace(/\s+/g, "")}` : undefined)}
      onClick={isCopyButton && !disabled ? () => onCopy(button) : undefined}
      rel={isUrlButton ? "noopener noreferrer" : undefined}
      target={isUrlButton ? "_blank" : undefined}
      type={isCopyButton ? "button" : undefined}
      sx={{
        alignItems: "center",
        appearance: "none",
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.custom.blue[200], 0.75)}`,
        borderRadius: radii.base,
        color: disabled ? theme.palette.text.disabled : theme.palette.custom.blue[500],
        cursor: isInteractive && !disabled ? "pointer" : "default",
        display: "flex",
        fontFamily: "inherit",
        fontSize: 12.5,
        fontWeight: 700,
        gap: 0.75,
        justifyContent: "center",
        minHeight: 34,
        outline: 0,
        px: 1.25,
        textAlign: "center",
        textDecoration: "none",
        width: "100%",
        "&:hover": isInteractive && !disabled
          ? {
              backgroundColor: alpha(theme.palette.custom.blue[100], theme.palette.mode === "dark" ? 0.12 : 0.5),
              borderColor: theme.palette.custom.blue[300],
            }
          : undefined,
        "&:focus-visible": isInteractive && !disabled
          ? {
              boxShadow: `0 0 0 3px ${theme.palette.focusRing}`,
            }
          : undefined,
      }}
    >
      <TemplateButtonIcon type={button.type} />
      <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </Box>
    </Box>
  );
}

function ErrorActionButton({
  children,
  disabled = false,
  href = "",
  icon,
  onClick,
}) {
  const theme = useTheme();
  const isLink = Boolean(href);

  return (
    <Box
      component={isLink ? "a" : "button"}
      disabled={!isLink ? disabled : undefined}
      href={isLink ? href : undefined}
      onClick={!isLink && !disabled ? onClick : undefined}
      rel={isLink ? "noopener noreferrer" : undefined}
      target={isLink ? "_blank" : undefined}
      type={!isLink ? "button" : undefined}
      sx={{
        alignItems: "center",
        appearance: "none",
        backgroundColor: getPreviewSurfaceColor(theme),
        border: `1px solid ${getPreviewBorderColor(theme)}`,
        borderRadius: radii.base,
        color: disabled ? theme.palette.text.disabled : theme.palette.custom.blue[500],
        cursor: disabled ? "default" : "pointer",
        display: "flex",
        flex: 1,
        fontFamily: "inherit",
        fontSize: 12.5,
        fontWeight: 700,
        gap: 0.75,
        justifyContent: "center",
        minHeight: 34,
        minWidth: 0,
        outline: 0,
        px: 1.25,
        textDecoration: "none",
        width: "100%",
        "&:hover": disabled
          ? undefined
          : {
              backgroundColor: getPreviewSurfaceColor(theme),
              borderColor: theme.palette.custom.blue[300],
            },
        "&:focus-visible": disabled
          ? undefined
          : {
              boxShadow: `0 0 0 3px ${theme.palette.focusRing}`,
            },
      }}
    >
      {icon}
      <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {children}
      </Box>
    </Box>
  );
}

function normalizeStatusText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function getMessageStatusKind(status, hasError) {
  if (hasError) return "failed";

  const normalized = normalizeStatusText(status);
  if (!normalized) return "pending";

  if (
    normalized.includes("nao entregue") ||
    normalized.includes("not_delivered") ||
    normalized.includes("not delivered") ||
    normalized.includes("falha") ||
    normalized.includes("failed") ||
    normalized.includes("failure") ||
    normalized.includes("erro") ||
    normalized.includes("error") ||
    normalized.includes("rejected") ||
    normalized.includes("rejeitada") ||
    normalized.includes("expired") ||
    normalized.includes("expirada")
  ) {
    return "failed";
  }

  if (normalized.includes("lida") || normalized.includes("read") || normalized.includes("visualizada")) {
    return "read";
  }

  if (normalized.includes("entregue") || normalized.includes("delivered")) {
    return "delivered";
  }

  if (
    normalized.includes("enviada") ||
    normalized.includes("enviado") ||
    normalized.includes("sent") ||
    normalized.includes("accepted") ||
    normalized.includes("concluida") ||
    normalized.includes("concluido") ||
    normalized.includes("nao confirmada")
  ) {
    return "sent";
  }

  if (
    normalized.includes("espera") ||
    normalized.includes("process") ||
    normalized.includes("andamento") ||
    normalized.includes("waiting") ||
    normalized.includes("pending")
  ) {
    return "pending";
  }

  return "pending";
}

function MessageStatusIcon({ hasError, status }) {
  const theme = useTheme();
  const kind = getMessageStatusKind(status, hasError);
  const neutralColor = theme.palette.text.secondary;

  if (kind === "failed") {
    return <ErrorOutlineOutlinedIcon titleAccess="Falha no envio" sx={{ color: theme.palette.error.main, fontSize: 14 }} />;
  }

  if (kind === "read") {
    return <DoneAllOutlinedIcon titleAccess="Lida" sx={{ color: theme.palette.custom.blue[500], fontSize: 14 }} />;
  }

  if (kind === "delivered") {
    return <DoneAllOutlinedIcon titleAccess="Entregue" sx={{ color: neutralColor, fontSize: 14 }} />;
  }

  if (kind === "sent") {
    return <DoneOutlinedIcon titleAccess="Enviada" sx={{ color: neutralColor, fontSize: 14 }} />;
  }

  return <AccessTimeOutlinedIcon titleAccess="Aguardando confirmação" sx={{ color: theme.palette.text.disabled, fontSize: 13 }} />;
}

function StatusMeta({ message, showStatusIcon = true }) {
  const theme = useTheme();
  const time = formatTime(message.sentAt);
  const hasError = Boolean(message.error);
  const shouldShowStatusIcon = showStatusIcon && (Boolean(message.status) || hasError);

  if (!time && !shouldShowStatusIcon) return null;

  return (
    <Stack direction="row" spacing={0.45} sx={{ alignItems: "center", justifyContent: "flex-end", mt: 0.5 }}>
      {time ? (
        <Typography sx={{ color: theme.palette.text.secondary, fontSize: 10.5, lineHeight: 1 }}>
          {time}
        </Typography>
      ) : null}
      {shouldShowStatusIcon ? (
        <MessageStatusIcon hasError={hasError} status={message.status} />
      ) : null}
    </Stack>
  );
}

function ErrorNotice({
  canRetry = false,
  error,
  onRetry,
  retrying = false,
  whatsappUrl = "",
}) {
  const theme = useTheme();
  if (!error) return null;
  const hasRetryAction = Boolean(canRetry && onRetry);
  const hasWhatsAppAction = Boolean(whatsappUrl);
  const hasActions = hasRetryAction || hasWhatsAppAction;

  return (
    <Stack
      spacing={1}
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.error.main, 0.16)}`,
        borderRadius: radii.base,
        color: theme.palette.text.secondary,
        mt: 1,
        p: 1.25,
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
        <ErrorOutlineOutlinedIcon sx={{ color: theme.palette.error.main, fontSize: 18, mt: "2px" }} />
        <Typography sx={{ fontSize: 12, lineHeight: 1.45 }}>
          <Box component="span" sx={{ color: theme.palette.text.secondary, fontStyle: "italic", fontWeight: 700 }}>
            Atenção:
          </Box>{" "}
          {error}
        </Typography>
      </Stack>
      {hasActions ? (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={0.75}>
          {hasRetryAction ? (
            <ErrorActionButton
              disabled={retrying}
              icon={<RefreshOutlinedIcon sx={{ fontSize: 17 }} />}
              onClick={onRetry}
            >
              {retrying ? "Tentando enviar..." : "Tentar enviar novamente"}
            </ErrorActionButton>
          ) : null}
          {hasWhatsAppAction ? (
            <ErrorActionButton
              href={whatsappUrl}
              icon={<WhatsAppIcon sx={{ fontSize: 17 }} />}
            >
              Abrir no WhatsApp
            </ErrorActionButton>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
}

export default function DeliveredMessagePreview({
  canRetry = false,
  fallbackError = "",
  fallbackChannel = "",
  fallbackSentAt = "",
  fallbackStatus = "",
  onRetry,
  preview,
  reserveTopActionSpace = false,
  recipientContact = "",
  retrying = false,
  showStatusIcon = true,
  sx,
  title = "",
  variables,
}) {
  const theme = useTheme();
  const [copyFeedback, setCopyFeedback] = React.useState({ id: "", status: "" });
  const message = normalizeDeliveredPreview(preview, fallbackChannel, fallbackSentAt, fallbackStatus, fallbackError, variables);
  const whatsappUrl = message.channel === "whatsapp"
    ? firstText(message.whatsappUrl, getWhatsAppOpenUrl(recipientContact, variables))
    : "";

  const handleCopy = React.useCallback((button) => {
    copyToClipboard(button.copyValue)
      .then(() => {
        setCopyFeedback({ id: button.id, status: "success" });
      })
      .catch(() => {
        setCopyFeedback({ id: button.id, status: "error" });
      })
      .finally(() => {
        window.setTimeout(() => {
          setCopyFeedback((current) => (current.id === button.id ? { id: "", status: "" } : current));
        }, 1600);
      });
  }, []);

  if (!hasPreviewContent(message)) return null;

  return (
    <Box sx={sx}>
      {title ? (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
          <Typography sx={{ color: theme.palette.text.primary, fontSize: 15, fontWeight: 600 }}>
            {title}
          </Typography>
        </Stack>
      ) : null}
      <Box
        className="DeliveredMessagePreview-surface"
        sx={{
          backgroundColor:
            getPreviewSurfaceColor(theme),
          border: `1px solid ${getPreviewBorderColor(theme)}`,
          borderRadius: radii.base,
          color: theme.palette.text.primary,
          minHeight: 148,
          p: 1.25,
        }}
      >
        <Box
          className="DeliveredMessagePreview-content"
          sx={reserveTopActionSpace ? { pr: 4 } : undefined}
        >
          {message.subject ? (
            <Typography sx={{ color: theme.palette.text.primary, fontSize: 12.5, fontWeight: 800, mb: 0.75 }}>
              {message.subject}
            </Typography>
          ) : null}
          {message.attachments.map((attachment) => (
            <MessageAttachment attachment={attachment} key={attachment.id} />
          ))}
          {message.headerText ? (
            <Typography sx={{ fontSize: 13, fontWeight: 800, lineHeight: 1.45, mb: 0.75 }}>
              {renderPreviewText(message.headerText, theme, message.channel, "header")}
            </Typography>
          ) : null}
          {message.text ? (
            <Typography
              component="div"
              sx={{
                color: theme.palette.text.primary,
                fontSize: 13,
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {renderPreviewText(message.text, theme, message.channel, "body")}
            </Typography>
          ) : null}
          {message.footer ? (
            <Typography
              component="div"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: 12,
                lineHeight: 1.4,
                mt: 0.9,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {renderPreviewText(message.footer, theme, message.channel, "footer")}
            </Typography>
          ) : null}
        </Box>
        <StatusMeta message={message} showStatusIcon={showStatusIcon} />
        {message.buttons.length ? (
          <Stack spacing={0.75} sx={{ mt: 1 }}>
            {message.buttons.map((button) => (
              <TemplateButton
                button={button}
                copyStatus={copyFeedback.id === button.id ? copyFeedback.status : ""}
                key={button.id}
                onCopy={handleCopy}
              />
            ))}
          </Stack>
        ) : null}
        <ErrorNotice
          canRetry={canRetry}
          error={message.error}
          onRetry={onRetry}
          retrying={retrying}
          whatsappUrl={whatsappUrl}
        />
      </Box>
    </Box>
  );
}
